'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { illustrationKeys } from '../queryKeys';
import { subscriptionKeys, PRICING_DEFAULTS } from '@/contexts/subscriptions';
import type { TokenPricingData } from '@/contexts/subscriptions';
import { createIllustration as createBookAction } from '../actions/createBook';
import { updateIllustration as updateBookAction } from '../actions/updateBook';
import { deleteIllustration as deleteBookAction } from '../actions/deleteBook';
import { toggleFavorite as toggleFavoriteAction } from '../actions/toggleFavorite';
import type { StoryAnalysisResult, Illustration } from '../types';

function optimisticDeductBalance(
  queryClient: ReturnType<typeof useQueryClient>,
  familyId: string,
  cost: number,
) {
  const balanceKey = subscriptionKeys.balance(familyId);
  const previousBalance = queryClient.getQueryData<{ balance: number }>(balanceKey);

  if (previousBalance) {
    queryClient.setQueryData(balanceKey, {
      ...previousBalance,
      balance: Math.max(0, previousBalance.balance - cost),
    });
  }

  return previousBalance;
}

export function useIllustrationMutations(familyId: string | null | undefined) {
  const queryClient = useQueryClient();

  const createIllustration = useMutation({
    mutationFn: (input: {
      storyId: string;
      title: string;
      style: string;
      format: string;
      storyAnalysis: StoryAnalysisResult;
      characterDecisions: Array<{
        extractedName: string;
        action: string;
        matchedCharacterId?: string;
        createData?: { name: string; description?: string };
        skipConfirmed?: boolean;
      }>;
    }) => createBookAction(input),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: illustrationKeys.list(familyId) });
      }
    },
  });

  const updateIllustration = useMutation({
    mutationFn: (params: {
      bookId: string;
      updates: Parameters<typeof updateBookAction>[1];
    }) => updateBookAction(params.bookId, params.updates),
    onSuccess: (_result, params) => {
      queryClient.invalidateQueries({ queryKey: illustrationKeys.detail(params.bookId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: illustrationKeys.list(familyId) });
      }
    },
  });

  const deleteIllustration = useMutation({
    mutationFn: (bookId: string) => deleteBookAction(bookId),
    onSuccess: () => {
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: illustrationKeys.list(familyId) });
      }
    },
  });

  const toggleFavorite = useMutation({
    mutationFn: (bookId: string) => toggleFavoriteAction(bookId),
    onMutate: async (bookId: string) => {
      // Cancel in-flight queries for all list variants and the detail
      await queryClient.cancelQueries({ queryKey: illustrationKeys.lists() });
      await queryClient.cancelQueries({ queryKey: illustrationKeys.detail(bookId) });

      // Snapshot existing data for rollback
      const previousDetail = queryClient.getQueryData<Illustration>(illustrationKeys.detail(bookId));

      // Optimistically flip the list cache (all-books + favorites list)
      const flipIllustration = (illustration: Illustration): Illustration => {
        if (illustration.id !== bookId) return illustration;
        const nowFav = !illustration.isFavorite;
        return {
          ...illustration,
          isFavorite: nowFav,
          favoritedAt: nowFav ? new Date().toISOString() : null,
        };
      };

      if (familyId) {
        const listKey = illustrationKeys.list(familyId);
        const favListKey = illustrationKeys.list(familyId, { onlyFavorites: true });
        const previousList = queryClient.getQueryData<Illustration[]>(listKey);
        const previousFavList = queryClient.getQueryData<Illustration[]>(favListKey);

        if (previousList) {
          queryClient.setQueryData<Illustration[]>(listKey, previousList.map(flipIllustration));
        }
        if (previousFavList) {
          // When filter is active, remove from list if we're unfavoriting
          const currentIllustration = previousDetail ?? previousList?.find(b => b.id === bookId);
          if (currentIllustration?.isFavorite) {
            // Unfavoriting — remove from favorites list
            queryClient.setQueryData<Illustration[]>(favListKey, previousFavList.filter(b => b.id !== bookId));
          } else {
            // Favoriting — add optimistically at start
            const flipped = flipIllustration(currentIllustration ?? { id: bookId } as Illustration);
            queryClient.setQueryData<Illustration[]>(favListKey, [flipped, ...previousFavList]);
          }
        }

        if (previousDetail) {
          queryClient.setQueryData<Illustration>(illustrationKeys.detail(bookId), flipIllustration(previousDetail));
        }

        return { previousList, previousFavList, previousDetail };
      }

      return { previousDetail };
    },
    onError: (_err, bookId, context) => {
      // Rollback on error
      if (familyId && context) {
        const ctx = context as {
          previousList?: Illustration[];
          previousFavList?: Illustration[];
          previousDetail?: Illustration;
        };
        if (ctx.previousList) {
          queryClient.setQueryData(illustrationKeys.list(familyId), ctx.previousList);
        }
        if (ctx.previousFavList) {
          queryClient.setQueryData(illustrationKeys.list(familyId, { onlyFavorites: true }), ctx.previousFavList);
        }
        if (ctx.previousDetail) {
          queryClient.setQueryData(illustrationKeys.detail(bookId), ctx.previousDetail);
        }
      }
    },
    onSettled: (_data, _err, bookId) => {
      // Always re-sync after mutation
      queryClient.invalidateQueries({ queryKey: illustrationKeys.detail(bookId) });
      if (familyId) {
        queryClient.invalidateQueries({ queryKey: illustrationKeys.list(familyId) });
        queryClient.invalidateQueries({ queryKey: illustrationKeys.list(familyId, { onlyFavorites: true }) });
      }
    },
  });

  return { createIllustration, updateIllustration, deleteIllustration, toggleFavorite };
}
